import { BarkClient } from "./bark.js";
import { BarkConfig, BarkOptions } from "./types.js";

/**
 * 创建Bark客户端实例的便捷函数
 * @param deviceKey 设备密钥
 * @param serverUrl 服务器地址（可选）
 * @returns BarkClient实例
 */
export function createBarkClient(
  deviceKey: string,
  serverUrl?: string
): BarkClient {
  return new BarkClient({
    deviceKey,
    serverUrl,
  });
}

/**
 * 快速发送推送消息的便捷函数
 * @param deviceKey 设备密钥
 * @param text 消息内容
 * @param title 消息标题（可选）
 * @param serverUrl 服务器地址（可选）
 * @returns Promise<BarkResponse>
 */
export async function quickPush(
  deviceKey: string,
  text: string,
  title?: string,
  serverUrl?: string
) {
  const client = createBarkClient(deviceKey, serverUrl);
  return client.sendText(text, title);
}

/**
 * 验证设备密钥格式
 * @param deviceKey 设备密钥
 * @returns boolean
 */
export function validateDeviceKey(deviceKey: string): boolean {
  // Bark设备密钥通常是22位的字符串
  const deviceKeyRegex = /^[a-zA-Z0-9_-]{20,25}$/;
  return deviceKeyRegex.test(deviceKey);
}

/**
 * 构建推送URL（用于GET请求方式）
 * @param config 客户端配置
 * @param options 推送选项
 * @returns string
 */
export function buildPushUrl(config: BarkConfig, options: BarkOptions): string {
  const baseUrl = config.serverUrl || "https://api.day.app";
  const { deviceKey } = config;

  let url = `${baseUrl}/${deviceKey}`;

  if (options.title) {
    url += `/${encodeURIComponent(options.title)}`;
  }

  url += `/${encodeURIComponent(options.body)}`;

  const params = new URLSearchParams();

  if (options.group) params.append("group", options.group);
  if (options.icon) params.append("icon", options.icon);
  if (options.sound) params.append("sound", options.sound);
  if (options.url) params.append("url", options.url);
  if (options.automaticallyCopy !== undefined)
    params.append("automaticallyCopy", options.automaticallyCopy.toString());
  if (options.copy !== undefined)
    params.append("copy", options.copy.toString());
  if (options.level) params.append("level", options.level);
  if (options.badge !== undefined)
    params.append("badge", options.badge.toString());
  if (options.isArchive !== undefined)
    params.append("isArchive", options.isArchive.toString());

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

/**
 * Bark声音名称常量
 */
export const BarkSounds = {
  // 系统声音
  DEFAULT: "default",
  BELL: "bell",
  BIRDSONG: "birdsong",
  BLOOM: "bloom",
  CALYPSO: "calypso",
  CHIME: "chime",
  CHOO: "choo",
  DESCENT: "descent",
  ELECTRONIC: "electronic",
  FANFARE: "fanfare",
  GLASS: "glass",
  GOTOSLEEP: "gotosleep",
  HEALTHNOTIFICATION: "healthnotification",
  HORN: "horn",
  LADDER: "ladder",
  MAILSENT: "mailsent",
  MINUET: "minuet",
  MULTIWAYINVITATION: "multiwayinvitation",
  NEWMAIL: "newmail",
  NEWSFLASH: "newsflash",
  NOIR: "noir",
  PAYMENTSUCCESS: "paymentsuccess",
  SHAKE: "shake",
  SHERWOODFOREST: "sherwoodforest",
  SILENCE: "silence",
  SPELL: "spell",
  SUSPENSE: "suspense",
  TELEGRAPH: "telegraph",
  TIPTOES: "tiptoes",
  TYPEWRITERS: "typewriters",
  UPDATE: "update",
} as const;

export type BarkSound = (typeof BarkSounds)[keyof typeof BarkSounds];
